/**
 * Points Store Types
 *
 * Event-log model for streak freezes and time machine repairs.
 * Streak state is computed as a pure function of (activity history,
 * freeze events, tm events). All events are append-only and made
 * idempotent via client-generated IDs.
 *
 * See STORE_SYSTEM.md for the full spec.
 */

export type FreezeType = 'normal' | 'weekend';

export type FreezeRefusalReason =
  | 'consecutive_day'      // Normal freeze cannot fire on consecutive days
  | 'single_weekend_day';  // Weekend freeze requires both weekend days missed

export type FreezeEvent =
  | {
      id: string;
      type: 'purchased';
      freezeType: FreezeType;
      ts: string;
    }
  | {
      id: string;
      type: 'fired';
      freezeType: FreezeType;
      ts: string;
      savedDays: string[]; // 1 entry for normal, 2 for weekend
    }
  | {
      id: string;
      type: 'refused';
      freezeType: FreezeType;
      ts: string;
      missedDay: string;
      refusalReason: FreezeRefusalReason;
    };

export interface TmEvent {
  id: string;
  type: 'repaired';
  ts: string;
  repairedDay: string;
  cost: number;
}

export type WeekendWindow = 'fri-sat' | 'sat-sun';

export interface StoreSettings {
  weekendWindow: WeekendWindow;
  equippedCursorBundleId?: string;
  ownedCursorBundleIds?: string[];
  equippedAvatarFrameId?: string;
  ownedAvatarFrameIds?: string[];
  equippedFinishEffectId?: string;
  ownedFinishEffectIds?: string[];
  equippedPremiumAvatarId?: string;
  ownedPremiumAvatarIds?: string[];
  equippedBackgroundId?: string;
  ownedBackgroundIds?: string[];
  equippedGadgetId?: string;
  ownedGadgetIds?: string[];
  /** Earned (box-dropped) preset emoji avatars. Free presets aren't tracked. */
  ownedPresetAvatarIds?: string[];
  /** Unopened surprise boxes the user holds, by box type. */
  boxes?: Partial<Record<import('./surpriseBoxes').BoxType, number>>;
  /** Consecutive-common counter driving the box pity timer. */
  boxPity?: number;
  /** Challenge badges earned (badge IDs). */
  earnedBadgeIds?: string[];
  /** One-time challenges already completed + rewarded. */
  completedChallengeIds?: string[];
  /** Daily-challenge claim stamp (YYYY-MM-DD + claimed). */
  dailyChallenge?: { date: string; claimed: boolean };
  /** Weekly-challenge claim stamp (week-start YYYY-MM-DD + claimed). */
  weeklyChallenge?: { weekStart: string; claimed: boolean };
  /**
   * Challenges only count activity at/after this ISO timestamp. Set once on the
   * first evaluation; historical play before it does not count (so tenured
   * players don't instantly complete everything and flood boxes).
   */
  challengeBaselineTs?: string;
  /**
   * One-time unlock for the custom-image avatar. Uploading your own picture is
   * the priciest, most bespoke cosmetic — gated behind a single purchase
   * (CUSTOM_UPLOAD_PRICE). Once unlocked the user can swap their image freely.
   */
  customUploadUnlocked?: boolean;
}

export interface FreezeInventory {
  equipped: { normal: number; weekend: number };
  totalDaysEquipped: number;
}

export type DaySavedState =
  | 'practiced'
  | 'missed'
  | 'freeze_saved'
  | 'tm_saved'
  | 'today';

export const EQUIPMENT_CAP_DAYS = 3;

export const FREEZE_DAYS_COVERAGE: Record<FreezeType, number> = {
  normal: 1,
  weekend: 2,
};

export const TM_LOOKBACK_DAYS = 30;
export const TM_COOLDOWN_DAYS = 14;

export const DEFAULT_WEEKEND_WINDOW: WeekendWindow = 'sat-sun';

/**
 * One-time cost to unlock the custom-image avatar. Sits at the top of the
 * buyable cosmetic ladder (above the 175-pt diamond frame) — a bespoke,
 * "committed" purchase rather than a day-one default. ~5 weeks of saving for a
 * normal ~8 pts/day player; ~13 days for an active one.
 */
export const CUSTOM_UPLOAD_PRICE = 300;
